import { Chip, InputAdornment, TextField } from '@mui/material';
import { Search } from 'lucide-react';
import React from 'react';
import type { IntegrationClient } from '../../types';
import { useTranslation } from 'react-i18next';

interface IntegrationClientListProps {
  clients: IntegrationClient[];
  selectedPublicId: string;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSelect: (publicId: string) => void;
}

export const IntegrationClientList: React.FC<IntegrationClientListProps> = ({
  clients,
  selectedPublicId,
  searchTerm,
  onSearchChange,
  onSelect,
}) => {
  const { t } = useTranslation();

  return (
    <div className="min-w-0">
      <TextField
        fullWidth
        size="small"
        label={t('apiKeys.integrationClients.searchClients')}
        placeholder={t('apiKeys.integrationClients.searchClientsPlaceholder')}
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={16} className="text-gray-400" />
            </InputAdornment>
          ),
        }}
      />
      <div className="mt-2 max-h-[420px] space-y-2 overflow-y-auto pr-1">
        {clients.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500 dark:border-slate-600 dark:text-slate-400">
            {t('apiKeys.integrationClients.noClientResults')}
          </div>
        ) : (
          clients.map((client) => {
            const selected = client.public_id === selectedPublicId;
            return (
              <button
                key={client.public_id}
                type="button"
                onClick={() => onSelect(client.public_id)}
                className={`w-full rounded-lg border p-3 text-left transition-colors ${
                  selected
                    ? 'border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-900/20'
                    : 'border-gray-200 hover:border-blue-300 dark:border-slate-700 dark:hover:border-blue-700'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-gray-900 dark:text-slate-100">
                      {client.name}
                    </div>
                    <div className="truncate font-mono text-xs text-gray-400 dark:text-slate-500">
                      {client.public_id}
                    </div>
                  </div>
                  <Chip
                    size="small"
                    label={client.status}
                    color={client.status === 'active' ? 'success' : 'default'}
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Chip size="small" label={client.environment} variant="outlined" />
                  <Chip
                    size="small"
                    label={t('apiKeys.integrationClients.bindingCount', {
                      count: client.binding_count ?? 0,
                    })}
                    variant="outlined"
                  />
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
